import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { ArrowLeft, UserCircle, CreditCard, ShoppingBag, Package, PackagePlus, Monitor, Truck, ShieldAlert, Wrench, AlertOctagon, MessageSquare, HelpCircle } from 'lucide-react';

const CATEGORY_CARDS = [
  { title: "Account Issue", icon: <UserCircle size={32} />, description: "Login, Registration, Verification", color: "#3b82f6", bg: "#eff6ff" },
  { title: "Payment Issue", icon: <CreditCard size={32} />, description: "Refunds, Billing, Invoices", color: "#8b5cf6", bg: "#f5f3ff" },
  { title: "Order Issue", icon: <ShoppingBag size={32} />, description: "Order Status, Cancellations", color: "#f59e0b", bg: "#fffbeb" },
  { title: "Product Issue", icon: <Package size={32} />, description: "Quality, Availability, Returns", color: "#10b981", bg: "#ecfdf5" },
  { title: "Technical Support", icon: <Monitor size={32} />, description: "App Crash, Slow Performance", color: "#6366f1", bg: "#eef2ff" },
  { title: "Delivery Issue", icon: <Truck size={32} />, description: "Tracking, Missing Packages", color: "#f97316", bg: "#fff7ed" },
  { title: "Seller Support", icon: <ShieldAlert size={32} />, description: "Inventory, Commissions", color: "#ec4899", bg: "#fdf2f8" },
  { title: "Service Request", icon: <Wrench size={32} />, description: "Reschedule, Feedback", color: "#14b8a6", bg: "#f0fdfa" },
  { title: "Complaint", icon: <AlertOctagon size={32} />, description: "File a formal complaint", color: "#ef4444", bg: "#fef2f2" },
  { title: "Feedback", icon: <MessageSquare size={32} />, description: "Suggestions, Improvements", color: "#84cc16", bg: "#f7fee7" },
  { title: "Other", icon: <HelpCircle size={32} />, description: "General enquiries", color: "#64748b", bg: "#f8fafc" },
];

export default function TicketCategory() {
  const navigate = useNavigate();
  const location = useLocation();
  const ticketsLeft = location.state?.ticketsLeft || 20;

  const handleSelectCategory = (categoryTitle: string) => {
    navigate('/tickets/create', { state: { category: categoryTitle, ticketsLeft } });
  };

  return (
    <div className="fade-in" style={{ width: '100%', paddingBottom: '40px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '32px' }}>
        <button onClick={() => navigate('/tickets')} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '8px', display: 'flex', alignItems: 'center', color: '#64748b' }}>
          <ArrowLeft size={20} />
        </button>
        <div>
          <h1 style={{ fontSize: '24px', fontWeight: '800', color: '#0f172a' }}>Select Ticket Category</h1>
          <p style={{ color: '#64748b', fontSize: '14px', marginTop: '4px' }}>Choose the category that best matches your issue</p>
        </div>
      </div>

      {/* ── Product Request — Featured Card ── */}
      <div
        onClick={() => navigate('/tickets/create', { state: { category: 'Add Product', productRequest: true, ticketsLeft } })}
        style={{
          background: 'linear-gradient(135deg, #0f172a 0%, #1e3a5f 60%, #0369a1 100%)',
          borderRadius: '20px',
          padding: '28px 32px',
          marginBottom: '28px',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          gap: '24px',
          boxShadow: '0 8px 24px -4px rgba(14,165,233,0.35)',
          transition: 'all 0.2s ease',
          position: 'relative',
          overflow: 'hidden',
        }}
        onMouseEnter={e => {
          e.currentTarget.style.transform = 'translateY(-3px)';
          e.currentTarget.style.boxShadow = '0 16px 32px -4px rgba(14,165,233,0.45)';
        }}
        onMouseLeave={e => {
          e.currentTarget.style.transform = 'translateY(0)';
          e.currentTarget.style.boxShadow = '0 8px 24px -4px rgba(14,165,233,0.35)';
        }}
      >
        {/* decorative circles */}
        <div style={{ position: 'absolute', right: '-20px', top: '-20px', width: '140px', height: '140px', borderRadius: '50%', background: 'rgba(255,255,255,0.05)' }} />
        <div style={{ position: 'absolute', right: '60px', bottom: '-40px', width: '100px', height: '100px', borderRadius: '50%', background: 'rgba(255,255,255,0.04)' }} />

        <div style={{ width: '72px', height: '72px', borderRadius: '16px', background: 'rgba(255,255,255,0.12)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
          <PackagePlus size={36} color="white" />
        </div>

        <div style={{ flex: 1 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '6px' }}>
            <h2 style={{ fontSize: '22px', fontWeight: '800', color: 'white', margin: 0 }}>Add Product Request</h2>
            <span style={{ background: '#22d3ee', color: '#0c4a6e', fontSize: '11px', fontWeight: '700', padding: '2px 10px', borderRadius: '20px', letterSpacing: '0.05em' }}>NEW REQUEST</span>
          </div>
          <p style={{ color: 'rgba(255,255,255,0.7)', fontSize: '14px', margin: 0 }}>Request a new product to be added to the catalog — fill in product details, images and warranty info</p>
        </div>

        <div style={{ background: 'rgba(255,255,255,0.15)', borderRadius: '12px', padding: '12px 20px', flexShrink: 0, color: 'white', fontWeight: '700', fontSize: '14px', backdropFilter: 'blur(4px)' }}>
          Start Request →
        </div>
      </div>

      <div style={{ 
        display: 'grid', 
        gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', 
        gap: '24px' 
      }}>
        {CATEGORY_CARDS.map((cat) => {
          const isProductCard = cat.title === 'Product Issue';
          return (
            <div 
              key={cat.title}
              onClick={() => handleSelectCategory(cat.title)}
              style={{
                background: isProductCard ? 'linear-gradient(145deg, #f0fdfa, #e6fff9)' : 'white',
                borderRadius: '16px',
                padding: '24px',
                border: isProductCard ? '2px solid #14b8a6' : '1px solid #e2e8f0',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                display: 'flex',
                flexDirection: 'column',
                gap: '16px',
                boxShadow: isProductCard ? '0 4px 12px -2px rgba(20,184,166,0.2)' : '0 4px 6px -1px rgba(0,0,0,0.05)',
                position: 'relative',
                overflow: 'hidden',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-4px)';
                e.currentTarget.style.boxShadow = isProductCard
                  ? '0 12px 24px -4px rgba(20,184,166,0.35)'
                  : '0 10px 15px -3px rgba(0,0,0,0.1)';
                e.currentTarget.style.borderColor = cat.color;
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = isProductCard
                  ? '0 4px 12px -2px rgba(20,184,166,0.2)'
                  : '0 4px 6px -1px rgba(0,0,0,0.05)';
                e.currentTarget.style.borderColor = isProductCard ? '#14b8a6' : '#e2e8f0';
              }}
            >
              {isProductCard && (
                <span style={{
                  position: 'absolute', top: '14px', right: '14px',
                  background: '#14b8a6', color: 'white',
                  fontSize: '10px', fontWeight: '700',
                  padding: '2px 8px', borderRadius: '20px', letterSpacing: '0.05em'
                }}>PRODUCT REQUEST</span>
              )}
              <div style={{ 
                width: '64px', 
                height: '64px', 
                borderRadius: '12px', 
                background: cat.bg, 
                color: cat.color,
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center' 
              }}>
                {cat.icon}
              </div>
              <div>
                <h3 style={{ fontSize: '18px', fontWeight: '700', color: '#1e293b', marginBottom: '6px' }}>{cat.title}</h3>
                <p style={{ fontSize: '14px', color: '#64748b', lineHeight: '1.5' }}>{cat.description}</p>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
